/**
 * Persistence layer for durable, resumable audio recording pipelines.
 *
 * A `RecordingSession` groups an ordered sequence of `AudioChunk` records
 * (raw 16-bit PCM audio), an optional multi-resolution `EnvelopePyramid`
 * (used for fast waveform rendering at various zoom levels), and a set of
 * `RegionAnnotation`s that label time intervals within the recording with
 * qualified phoneme labels plus percussive/pitch-invariant metadata.
 */
import { del, delByIndex, get, getAll, getAllByIndex, putBulk, putRecord, } from "./db.js";
import { appScopedKey } from "./namespace.js";
const SESSIONS_STORE = "recording-sessions";
const CHUNKS_STORE = "recording-chunks";
const ENVELOPES_STORE = "recording-envelopes";
function chunkId(sessionId, chunkIndex) {
    return `${sessionId}:${chunkIndex}`;
}
/**
 * Create and persist a new recording session, scoped to the current app
 * namespace.
 */
export async function createSession(options) {
    const now = Date.now();
    const session = {
        id: appScopedKey(options.id),
        appId: appScopedKey(""),
        createdAt: now,
        updatedAt: now,
        status: "recording",
        sampleRate: options.sampleRate,
        channels: options.channels ?? 1,
        totalSamples: 0,
        annotations: [],
    };
    await putRecord(SESSIONS_STORE, session);
    console.info(`[recordings] created session '${session.id}'`);
    return session;
}
/** Load a recording session by id, or `undefined` if not found. */
export async function getSession(sessionId) {
    return get(SESSIONS_STORE, sessionId);
}
/** List all recording sessions. */
export async function listSessions() {
    return getAll(SESSIONS_STORE);
}
/** Update mutable session fields (status, updatedAt bump automatic). */
export async function updateSessionStatus(sessionId, status) {
    const session = await getSession(sessionId);
    if (!session) {
        throw new Error(`[recordings] session not found: ${sessionId}`);
    }
    session.status = status;
    session.updatedAt = Date.now();
    await putRecord(SESSIONS_STORE, session);
}
/**
 * Append a new chunk of raw PCM audio to a session, updating the session's
 * `totalSamples` counter. Chunk indices must be appended in order starting
 * from 0; callers are responsible for tracking the next index (e.g. via
 * `session.totalSamples` bookkeeping or a local counter).
 */
export async function appendChunk(sessionId, chunkIndex, pcm) {
    const session = await getSession(sessionId);
    if (!session) {
        throw new Error(`[recordings] session not found: ${sessionId}`);
    }
    const chunk = {
        id: chunkId(sessionId, chunkIndex),
        sessionId,
        chunkIndex,
        startSample: session.totalSamples,
        sampleCount: pcm.length,
        pcm,
    };
    await putRecord(CHUNKS_STORE, chunk);
    session.totalSamples += pcm.length;
    session.updatedAt = Date.now();
    await putRecord(SESSIONS_STORE, session);
    console.debug(`[recordings] appended chunk ${chunkIndex} to '${sessionId}' ` +
        `(${pcm.length} samples)`);
    return chunk;
}
/** Retrieve all chunks for a session, ordered by chunk index. */
export async function getAllChunks(sessionId) {
    const chunks = await getAllByIndex(CHUNKS_STORE, "sessionId", sessionId);
    return chunks.sort((a, b) => a.chunkIndex - b.chunkIndex);
}
/**
 * Concatenate all chunks for a session into a single contiguous PCM buffer.
 */
export async function getFullAudio(sessionId) {
    const chunks = await getAllChunks(sessionId);
    const totalLength = chunks.reduce((sum, c) => sum + c.sampleCount, 0);
    const out = new Int16Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
        out.set(chunk.pcm, offset);
        offset += chunk.sampleCount;
    }
    return out;
}
/**
 * Retrieve a slice of a session's audio as a contiguous PCM buffer, given a
 * sample range `[startSample, endSample)`. Reads only the chunks that
 * overlap the requested range.
 */
export async function getAudioRange(sessionId, startSample, endSample) {
    if (endSample <= startSample)
        return new Int16Array(0);
    const chunks = await getAllChunks(sessionId);
    const out = new Int16Array(endSample - startSample);
    for (const chunk of chunks) {
        const chunkStart = chunk.startSample;
        const chunkEnd = chunk.startSample + chunk.sampleCount;
        const overlapStart = Math.max(chunkStart, startSample);
        const overlapEnd = Math.min(chunkEnd, endSample);
        if (overlapStart >= overlapEnd)
            continue;
        const srcOffset = overlapStart - chunkStart;
        const srcLength = overlapEnd - overlapStart;
        const dstOffset = overlapStart - startSample;
        out.set(chunk.pcm.subarray(srcOffset, srcOffset + srcLength), dstOffset);
    }
    return out;
}
/** Delete a session, its chunks, and its envelope pyramid. */
export async function deleteSession(sessionId) {
    await delByIndex(CHUNKS_STORE, "sessionId", sessionId);
    await del(ENVELOPES_STORE, sessionId);
    await del(SESSIONS_STORE, sessionId);
    console.info(`[recordings] deleted session '${sessionId}'`);
}
/** Save (overwrite) the envelope pyramid for a session. */
export async function saveEnvelopePyramid(pyramid) {
    await putRecord(ENVELOPES_STORE, {
        ...pyramid,
        updatedAt: Date.now(),
    });
    console.debug(`[recordings] saved envelope pyramid for '${pyramid.sessionId}' ` +
        `levels=${pyramid.levels.length}`);
}
/** Load the envelope pyramid for a session, if one has been computed. */
export async function loadEnvelopePyramid(sessionId) {
    return get(ENVELOPES_STORE, sessionId);
}
/**
 * Replace all region annotations for a session with the given list, then
 * persist the updated session record.
 */
export async function saveRegionAnnotations(sessionId, annotations) {
    const session = await getSession(sessionId);
    if (!session) {
        throw new Error(`[recordings] session not found: ${sessionId}`);
    }
    session.annotations = annotations;
    session.updatedAt = Date.now();
    await putRecord(SESSIONS_STORE, session);
    console.debug(`[recordings] saved ${annotations.length} region annotation(s) for ` +
        `'${sessionId}'`);
}
/** Load region annotations for a session (empty array if none). */
export async function loadRegionAnnotations(sessionId) {
    const session = await getSession(sessionId);
    return session?.annotations ?? [];
}
/** Add or update a single region annotation, keyed by its `id`. */
export async function upsertRegionAnnotation(sessionId, annotation) {
    const session = await getSession(sessionId);
    if (!session) {
        throw new Error(`[recordings] session not found: ${sessionId}`);
    }
    const idx = session.annotations.findIndex((a) => a.id === annotation.id);
    if (idx >= 0) {
        session.annotations[idx] = annotation;
    }
    else {
        session.annotations.push(annotation);
    }
    session.updatedAt = Date.now();
    await putRecord(SESSIONS_STORE, session);
}
/** Remove a single region annotation by id. */
export async function removeRegionAnnotation(sessionId, annotationId) {
    const session = await getSession(sessionId);
    if (!session)
        return;
    session.annotations = session.annotations.filter((a) => a.id !== annotationId);
    session.updatedAt = Date.now();
    await putRecord(SESSIONS_STORE, session);
}
/**
 * Persist multiple chunks in a single transaction. Useful for bulk imports
 * or migrations; does not update the owning session's `totalSamples`.
 */
export async function putChunksBulk(chunks) {
    await putBulk(CHUNKS_STORE, chunks);
}
